import {
  ArrowClockwise20Regular,
  Mic28Filled,
  MicOff28Regular,
  MicProhibited28Regular,
} from "@fluentui/react-icons";
import { useCallback } from "react";

import { useDesktopBridge } from "../../../../features/desktop-bridge/use-desktop-bridge.js";
import type { TableCallStatus } from "../../../../features/table-call/table-call.context.js";
import { usePushToTalkButton } from "../../../../features/table-call/use-push-to-talk-button.js";
import { useTableCall } from "../../../../features/table-call/use-table-call.js";

const ignoreMicError = (_error: unknown): void => {
  // Mic toggle failures are reflected through the realtime call state.
};

const MicrophoneStatusIcon = ({
  callStatus,
  microphoneActive,
  canControlMicrophone,
}: {
  callStatus: TableCallStatus;
  microphoneActive: boolean;
  canControlMicrophone: boolean;
}) => {
  if (callStatus === "joining" || callStatus === "leaving") {
    return (
      <ArrowClockwise20Regular className="text-bzd-purple-500 h-[30px] animate-spin" />
    );
  }

  if (!canControlMicrophone) {
    return <MicProhibited28Regular className="h-[30px]" />;
  }

  return (
    <div className="relative">
      {Boolean(microphoneActive) && (
        <span className="bg-bzd-green-500 absolute top-0 right-[-5px] size-2 rounded-full" />
      )}
      {microphoneActive ? (
        <Mic28Filled className="h-[30px]" />
      ) : (
        <MicOff28Regular className="h-[30px]" />
      )}
    </div>
  );
};

export const MicrophoneControl = () => {
  const {
    status: callStatus,
    canControlMicrophone,
    canEnableMicrophone,
    microphoneActive,
    microphoneAlwaysOn: alwaysOnEnabled,
    setMicrophoneAlwaysOn: setAlwaysOnEnabled,
    startPushToTalk,
    stopPushToTalk,
  } = useTableCall();
  const { status: bridgeStatus } = useDesktopBridge();
  const isDesktopBridgeActive = bridgeStatus === "ready";

  const handlePushToTalkButton = useCallback(
    async (enabled: boolean) => {
      if (alwaysOnEnabled || (enabled && !canEnableMicrophone)) {
        return;
      }

      try {
        await (enabled ? startPushToTalk() : stopPushToTalk());
      } catch (error) {
        ignoreMicError(error);
      }
    },
    [alwaysOnEnabled, canEnableMicrophone, startPushToTalk, stopPushToTalk]
  );

  usePushToTalkButton(handlePushToTalkButton, {
    isEnabled:
      canControlMicrophone && !isDesktopBridgeActive && !alwaysOnEnabled,
  });

  const handleAlwaysOnSwitch = useCallback(async () => {
    const nextAlwaysOnEnabled = !alwaysOnEnabled;
    if (nextAlwaysOnEnabled ? !canEnableMicrophone : !canControlMicrophone) {
      return;
    }

    try {
      await setAlwaysOnEnabled(nextAlwaysOnEnabled);
    } catch (error) {
      ignoreMicError(error);
    }
  }, [
    canControlMicrophone,
    canEnableMicrophone,
    alwaysOnEnabled,
    setAlwaysOnEnabled,
  ]);

  return (
    <div className="flex flex-col items-center">
      <button
        className="flex cursor-pointer flex-col items-center"
        disabled={!canControlMicrophone}
        onClick={handleAlwaysOnSwitch}
        type="button"
      >
        <MicrophoneStatusIcon
          callStatus={callStatus}
          canControlMicrophone={canControlMicrophone}
          microphoneActive={microphoneActive}
        />
        <span className="text-sm select-none">Mic</span>
      </button>
    </div>
  );
};
